import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  LinearProgress,
  Paper,
  Typography,
  Chip,
  Snackbar,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Container,
  ThemeProvider,
  createTheme,
  CssBaseline,
  Card,
  CardContent,
} from '@mui/material';
import { CheckCircle, Error, Download, InfoOutlined } from '@mui/icons-material';
import TipsAndUpdatesOutlinedIcon from '@mui/icons-material/TipsAndUpdatesOutlined';
import { downloadCleanedFile, downloadEnrichedFile, pollJobUntilComplete } from '../../api/upload';
import gesLogo from './ges-logos.png';

const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#1B3B58',
      light: '#81B3C0',
      dark: '#1B3B58',
    },
    secondary: {
      main: '#DD7C25',
    },
  },
  typography: {
    fontFamily: '"Inter", "Segoe UI", "Roboto", sans-serif',
    h4: {
      fontWeight: 700,
      letterSpacing: '-0.5px',
    },
    h6: {
      fontWeight: 600,
    },
  },
  shape: {
    borderRadius: 16,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 12,
          fontWeight: 600,
        },
      },
    },
  },
});

const JobStatus = () => {
  const { jobId } = useParams();
  const fileName = localStorage.getItem(`job_filename_${jobId}`) || jobId;
  const [jobStatus, setJobStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [showResults, setShowResults] = useState(false);

  useEffect(() => {
    const fetchJobStatus = async () => {
      try {
        const status = await pollJobUntilComplete(jobId, (progress) => {
          console.log(`Job ${jobId} - ${progress.current_step}: ${progress.progress}%`);
        });
        setJobStatus(status);
        setJobStatus(status);
        console.log('jobStatus:', JSON.stringify(status, null, 2));
        setShowResults(true);
        // If the job finished with an error status, show the backend-provided message
        if (status.status === 'error') {
          setSnackbar({
            open: true,
            message: status.error || 'Job failed. Please check the job details.',
            severity: 'error',
          });
        }
      } catch (error) {
        console.error('Failed to fetch job status:', error);
        setSnackbar({
          open: true,
          message: 'Failed to load job status. Please try again.',
          severity: 'error',
        });
      } finally {
        setLoading(false);
      }
    };

    fetchJobStatus();
  }, [jobId]);

  const handleDownload = async (blobPath, type) => {
    try {
      if (type === 'cleaned') {
        await downloadCleanedFile(blobPath);
      } else {
        await downloadEnrichedFile(blobPath);
      }
      setSnackbar({
        open: true,
        message: `Downloaded: ${blobPath.split('/').pop()}`,
        severity: 'success',
      });
    } catch (error) {
      console.error('Download failed:', error);
      setSnackbar({
        open: true,
        message: 'Download failed. Please try again.',
        severity: 'error',
      });
    }
  };

  if (loading) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(27, 59, 88, 0.95)',
            backdropFilter: 'blur(8px)',
            zIndex: 9999,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            animation: 'fadeIn 0.3s ease-in',
            '@keyframes fadeIn': {
              from: { opacity: 0 },
              to: { opacity: 1 },
            },
          }}
        >
          <Box
            sx={{
              textAlign: 'center',
              p: 4,
              maxWidth: 500,
            }}
          >
            <Box
              sx={{
                width: 120,
                height: 120,
                mx: 'auto',
                mb: 4,
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #DD7C25 0%, #B45B2C 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                animation: 'pulse 2s ease-in-out infinite',
                boxShadow: '0 0 40px rgba(221, 124, 37, 0.5)',
                '@keyframes pulse': {
                  '0%, 100%': { transform: 'scale(1)', opacity: 1 },
                  '50%': { transform: 'scale(1.05)', opacity: 0.8 },
                },
              }}
            >
              <Download sx={{ fontSize: 60, color: 'white' }} />
            </Box>

            <Typography
              variant="h5"
              sx={{
                color: 'white',
                fontWeight: 700,
                mb: 2,
                animation: 'fadeInUp 0.5s ease-out',
                '@keyframes fadeInUp': {
                  from: { opacity: 0, transform: 'translateY(20px)' },
                  to: { opacity: 1, transform: 'translateY(0)' },
                },
              }}
            >
              Processing File: {fileName}
            </Typography>

            <Typography
              variant="body1"
              sx={{
                color: 'rgba(255, 255, 255, 0.8)',
                mb: 4,
                lineHeight: 1.6,
              }}
            >
              Our intelligent agent is analyzing and optimizing your data.
              This may take a few moments...
            </Typography>

            <LinearProgress
              sx={{
                height: 8,
                borderRadius: 4,
                background: 'rgba(255, 255, 255, 0.2)',
                '& .MuiLinearProgress-bar': {
                  background: 'linear-gradient(90deg, #DD7C25 0%, #B45B2C 100%)',
                  borderRadius: 4,
                },
              }}
            />

            <Typography
              variant="caption"
              sx={{
                color: 'rgba(255, 255, 255, 0.6)',
                display: 'block',
                mt: 2,
                fontStyle: 'italic',
              }}
            >
              Please do not close this window
            </Typography>
          </Box>
        </Box>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box sx={{ minHeight: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <Box
          component="img"
          src={gesLogo}
          alt="GES Logo"
          sx={{
            position: 'absolute',
            top: 20,
            left: 20,
            height: 60,
            width: 'auto',
            objectFit: 'contain',
            zIndex: 10,
            filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.3))',
          }}
        />

        <Container maxWidth="lg">
          <Box sx={{ maxWidth: 900, mx: 'auto' }}>
            <Paper
              elevation={0}
              sx={{
                p: 5,
                mb: 4,
                borderRadius: 4,
                background: 'rgba(255, 248, 248, 0.95)',
                backdropFilter: 'blur(20px)',
                border: '1px solid rgba(255,255,255,0.3)',
                boxShadow:
                  '0 20px 60px rgba(0,0,0,0.15), 0 0 0 1px rgba(255,255,255,0.5) inset',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow:
                    '0 30px 80px rgba(0,0,0,0.2), 0 0 0 1px rgba(255,255,255,0.5) inset',
                },
              }}
            >
              <Box sx={{ textAlign: 'center', mb: 4 }}>
                <Typography
                  variant="h4"
                  sx={{
                    mb: 1.5,
                    color: '#1B3B58',
                    fontWeight: 800,
                  }}
                >
                  
                  File : {jobStatus.original_filename}
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: '#1B3B58',
                    fontSize: '1rem',
                    maxWidth: 600,
                    mx: 'auto',
                  }}
                >
                  Processing complete. View results below.
                </Typography>
              </Box>

              <Divider sx={{ mb: 4, borderColor: 'rgba(0,0,0,0.06)' }} />

              {/* Temporary Results */}
              {showResults && jobStatus && (
                <Box sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <TipsAndUpdatesOutlinedIcon sx={{ color: '#1B3B58', fontSize: 20 }} />
                    <Typography
                      variant="subtitle2"
                      sx={{
                        fontWeight: 700,
                        color: '#334155',
                        fontSize: '0.8rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                      }}
                    >
                      Processing Results
                    </Typography>
                  </Box>
                  <List sx={{ p: 0 }}>
                    <ListItem
                      sx={{
                        mb: 1,
                        background:
                          jobStatus.status === 'completed'
                            ? 'linear-gradient(135deg, #dcfce7 0%, #f0fdf4 100%)'
                            : 'linear-gradient(135deg, #fee2e2 0%, #fef2f2 100%)',
                        borderRadius: 3,
                        border: jobStatus.status === 'completed' ? '1px solid #86efac' : '1px solid #fca5a5',
                        py: 2,
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 40 }}>
                        {jobStatus.status === 'completed' ? (
                          <CheckCircle sx={{ color: '#16a34a', fontSize: 28 }} />
                        ) : (
                          <Error sx={{ color: '#dc2626', fontSize: 28 }} />
                        )}
                      </ListItemIcon>
                      <ListItemText
                        primary={jobStatus.result?.filename || jobStatus.original_filename}
                        secondary={
                          jobStatus.status === 'completed'
                            ? `Processed successfully • ${jobStatus.result?.file_size_kb} KB`
                            : 'Processing failed'
                        }
                        primaryTypographyProps={{ fontWeight: 600, noWrap: true, color: '#1e293b' }}
                        secondaryTypographyProps={{
                          color: jobStatus.status === 'completed' ? '#16a34a' : '#dc2626',
                          fontWeight: 500,
                        }}
                      />
                      <Chip
                        label={jobStatus.status === 'completed' ? 'Success' : 'Failed'}
                        size="small"
                        sx={{
                          fontWeight: 700,
                          background: jobStatus.status === 'completed' ? '#16a34a' : '#dc2626',
                          color: 'white',
                        }}
                      />
                    </ListItem>
                  </List>

                  {jobStatus.status === 'error' && jobStatus.error && (
                    <Box sx={{ mt: 2 }}>
                      <Alert severity="error" variant="filled" sx={{ borderRadius: 2 }}>
                        {jobStatus.error}
                      </Alert>
                    </Box>
                  )}

                </Box>
              )}

              {/* Download Links */}
              {jobStatus && jobStatus.result && (
                <Box sx={{ mb: 3 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      mb: 2,
                      fontWeight: 700,
                      color: '#1B3B58',
                      fontSize: '1rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                    }}
                  >
                    📂 Download Processed Files
                  </Typography>
                  <Box
                    sx={{
                      mb: 2,
                      p: 2.5,
                      background: 'linear-gradient(135deg, #fdfeffff 0%, #c5e4ebff 20%, #e8e0a1ff 40%, #f7f8b1ff 60%, #F2A85C 80%, #D98C6E 100%)',
                      borderRadius: 3,
                      border: '1px solid rgba(255, 106, 106, 1)',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        borderColor: '#DD7C25',
                        boxShadow: '0 4px 16px rgba(221, 124, 37, 0.2)',
                        transform: 'translateY(-2px)',
                      },
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{
                        fontWeight: 700,
                        color: '#1B3B58',
                        mb: 1.5,
                        fontSize: '0.9rem',
                      }}
                    >
                      {jobStatus.result.filename}
                    </Typography>

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      {/* Cleaned File Link */}
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.5,
                          p: 1.5,
                          background: 'rgba(255, 255, 255, 0.7)',
                          borderRadius: 2,
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            background: 'rgba(255, 255, 255, 0.9)',
                            transform: 'translateX(4px)',
                          },
                        }}
                        onClick={() => handleDownload(jobStatus.result.cleaned_blob, 'cleaned')}
                      >
                        <Download sx={{ fontSize: 20, color: '#16a34a' }} />
                        <Box sx={{ flex: 1 }}>
                          <Typography
                            sx={{
                              color: '#1B3B58',
                              fontWeight: 600,
                              fontSize: '0.85rem',
                            }}
                          >
                            {jobStatus.result.cleaned_blob.split('/').pop()}
                          </Typography>
                          <Typography
                            sx={{
                              color: '#64748b',
                              fontSize: '0.75rem',
                            }}
                          >
                            Excel format (.xlsx)
                          </Typography>
                        </Box>
                        <Chip
                          label="Excel"
                          size="small"
                          sx={{
                            background: '#16a34a',
                            color: 'white',
                            fontWeight: 600,
                            fontSize: '0.7rem',
                          }}
                        />
                      </Box>

                      {/* Enriched File Link */}
                      {jobStatus.result.enriched_blob && (
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5,
                            p: 1.5,
                            background: 'rgba(255, 255, 255, 0.7)',
                            borderRadius: 2,
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              background: 'rgba(255, 255, 255, 0.9)',
                              transform: 'translateX(4px)',
                            },
                          }}
                          onClick={() => handleDownload(jobStatus.result.enriched_blob, 'enriched')}
                        >
                          <Download sx={{ fontSize: 20, color: '#2563eb' }} />
                          <Box sx={{ flex: 1 }}>
                            <Typography
                              sx={{
                                color: '#1B3B58',
                                fontWeight: 600,
                                fontSize: '0.85rem',
                              }}
                            >
                              {jobStatus.result.enriched_blob.split('/').pop()}
                            </Typography>
                            <Typography
                              sx={{
                                color: '#64748b',
                                fontSize: '0.75rem',
                              }}
                            >
                              Excel format (.xlsx)
                            </Typography>
                          </Box>
                          <Chip
                            label="Excel"
                            size="small"
                            sx={{
                              background: '#2563eb',
                              color: 'white',
                              fontWeight: 600,
                              fontSize: '0.7rem',
                            }}
                          />
                        </Box>
                      )}
                    </Box>
                  </Box>
                </Box>
              )}

              {/* Summary */}
              {jobStatus && jobStatus.status === 'completed' && jobStatus.result?.summary && (
                <Box sx={{ mt: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <TipsAndUpdatesOutlinedIcon sx={{ color: '#1B3B58', fontSize: 20 }} />
                    <Typography
                      variant="subtitle2"
                      sx={{
                        fontWeight: 700,
                        color: '#1B3B58',
                        fontSize: '1rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                      }}
                    >
                      Data Cleaning Summary
                    </Typography>
                  </Box>
                  <Card
                    sx={{
                      mb: 2,
                      background: 'linear-gradient(135deg, #fdfeffff 0%, #c5e4ebff 20%, #C5BE86 40%, #F3F57A 60%, #F2A85C 80%, #D98C6E 100%)',
                      border: '1px solid #ff7979ff',
                      borderRadius: 3,
                      boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
                        transform: 'translateY(-2px)',
                      },
                    }}
                  >
                    <CardContent sx={{ p: 3, '&:last-child': { pb: 3 } }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                        <Box
                          sx={{
                            width: 36,
                            height: 36,
                            borderRadius: 2,
                            background: 'linear-gradient(135deg, #1B3B58 0%, #2d5a7b 100%)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <InfoOutlined sx={{ color: 'white', fontSize: 20 }} />
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <Typography
                            variant="subtitle2"
                            sx={{
                              fontWeight: 700,
                              color: '#1B3B58',
                              fontSize: '0.875rem',
                              letterSpacing: '0.3px',
                            }}
                          >
                            {jobStatus.result.filename}
                          </Typography>
                        </Box>
                      </Box>
                      <Typography
                        variant="body2"
                        sx={{
                          color: '#5e2a56ff',
                          lineHeight: 1.7,
                          fontSize: '0.875rem',
                          fontWeight: 400,
                          pl: 1,
                          borderLeft: '3px solid #db9121ff',
                          background: '#f8fafc',
                          p: 2,
                          borderRadius: 2,
                          whiteSpace: 'pre-wrap',
                        }}
                      >
                        {jobStatus.result.summary}
                      </Typography>
                    </CardContent>
                  </Card>
                </Box>
              )}
            </Paper>
          </Box>
        </Container>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
            variant="filled"
            elevation={6}
            sx={{ borderRadius: 3, fontWeight: 600 }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </ThemeProvider>
  );
};

export default JobStatus;
